import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { ChangeReview } from '@outdoria/types';
import { fetcher } from '../utils';
import { ADMIN_REVIEW_ALL, ADMIN_REVIEW_DELETE } from '../queryKeys';

type ChangeReviewAggregated = {
  affectedEntityId: string;
  documents: ChangeReview[];
};
export const useAdminChangeReviewApi = () => {
  const getChangeReview = async (): Promise<ChangeReviewAggregated[]> => {
    const res = await fetcher('/v1/admin/misc/review');
    return res.json();
  };

  const {
    data = [],
    isLoading,
    isFetched,
    isError,
  } = useSuspenseQuery<
    ChangeReviewAggregated[],
    unknown,
    ChangeReviewAggregated[],
    string[]
  >({
    queryKey: ADMIN_REVIEW_ALL,
    queryFn: getChangeReview,
  });

  return { data, isLoading, isFetched, isError };
};

// Approve all changes for the affected entity
export const useAdminApproveChanges = () => {
  const queryClient = useQueryClient();

  const adminApproveChanges = async (
    affectedEntityId: string,
  ): Promise<void> => {
    const response = await fetcher(
      `/v1/admin/misc/review/${affectedEntityId}`,
      {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (response.status !== 200 && response.status !== 201)
      throw new Error((await response.json()).error ?? 'Did not work!');
    queryClient.invalidateQueries({ queryKey: ADMIN_REVIEW_ALL });
  };

  const { mutate, error, status, isPending, isSuccess, isError } = useMutation({
    mutationKey: ADMIN_REVIEW_DELETE,
    mutationFn: adminApproveChanges,
  });

  return { mutate, error, status, isPending, isSuccess, isError };
};
